import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { StyledTextField, CardBase } from "../../components/global";
import { Project, ProjectStatus, ProjectTask } from "../../types/project";
import { styled } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAddProjectMutation,
  useEditProjectMutation,
  useGetProjectByIdQuery,
} from "../../services/project.service";
import { useTitle } from "../../hooks/useTitle";
import { Controller, useForm } from "react-hook-form";
import useScroll from "../../hooks/useScroll";
import { useUILabel } from "../../hooks/useUILabel";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import SelectLocation from "../../components/SelectLocation";
import SelectSkills from "../../components/SelectSkills";
import SelectManager from "../../components/SelectManager";
import { DesktopDateTimePicker, MobileDateTimePicker } from "@mui/x-date-pickers";
import SelectCertifications from "../../components/SelectCertification";
import ModalTaskForm from "./ModalTaskForm";
import StickyBox from "react-sticky-box";
import { Add, Delete, UploadFileOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import LocationModalForm from "../location/LocationModalForm";
import { Location } from "../../types/location";
import { Customer } from "../../types/customer";
import SelectTemplate from "../../components/SelectTemplate";
import { useGetTemplateByIdQuery } from "../../services/template.service";
import SelectTimezone from "../../components/SelectTimezone";
import {
  useAddProjectTaskMutation,
  useDeleteProjectTaskMutation,
} from "../../services/projectTask.service";
import { decode } from "html-entities";
import ProjectEditCustomizeLayoutModal, {
  PROJECT_EDIT_CARD_IDS,
  DEFAULT_PROJECT_EDIT_CARD_ORDER,
} from "../../components/ProjectEditCustomizeLayoutModal";
import {
  useGetCardPreferencesQuery,
  useUpdateCardPreferencesMutation,
  CARD_PREFERENCE_TYPE,
  CARD_PREFERENCE_PAGE,
} from "../../services/cardPreferences.service";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { Settings } from "@mui/icons-material";

const myTimezone = dayjs.tz.guess();


const StyledLoadingButton = styled(LoadingButton)(({ theme }) => {
  const primaryColor = theme.palette.primary.main;
  const contrastColor = theme.palette.getContrastText(primaryColor);

  return {
    borderRadius: 12,
    padding: "12px 32px",
    fontSize: "1rem",
    fontWeight: 700,
    textTransform: "none",
    letterSpacing: "0.4px",
    background: `linear-gradient(135deg, ${primaryColor} 0%, ${theme.palette.primary.dark} 100%)`,
    color: contrastColor,
    boxShadow: `0 6px 24px ${theme.palette.primary.main}4d`,
    transition: "all 0.25s ease",
    border: `1.5px solid ${theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.primary.dark}`,
    "&:hover": {
      transform: "translateY(-3px)",
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
      boxShadow: `0 10px 28px ${theme.palette.primary.main}66`,
      color: contrastColor,
      borderColor:
        theme.palette.mode === "dark"
          ? theme.palette.primary.light
          : theme.palette.primary.dark,
    },
    "&:focus-visible": {
      outline: `3px solid ${theme.palette.primary.light}88`,
      outlineOffset: "2px",
    },
    "&.Mui-disabled": {
      background: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
      boxShadow: "none",
      borderColor: "transparent",
    },
  };
});

const StyledNavigationList = styled(List)(() => ({
  padding: 0,
  margin: 0,
}));

const StyledNavigationButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: 10,
  margin: "6px 0",
  padding: "12px 18px",
  minHeight: 48,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    margin: "4px 0",
    padding: "10px 14px",
    minHeight: 44,
    borderRadius: 8,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: "3px",
    background: theme.palette.primary.main,
    transform: "scaleY(0)",
    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    borderRadius: "0 3px 3px 0",
    [theme.breakpoints.down("sm")]: {
      width: "2px",
    },
  },
  "&:hover": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(0, 0, 0, 0.05)",
    transform: "translateX(6px)",
    [theme.breakpoints.down("sm")]: {
      transform: "translateX(4px)",
    },
    boxShadow: theme.palette.mode === "dark"
      ? "0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
      : "0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
    "&::before": {
      transform: "scaleY(1)",
    },
    "& .MuiListItemText-primary": {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
  "& .MuiListItemText-primary": {
    fontSize: "0.95rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.875rem",
    },
  },
}));

const ProjectEditPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const selectLocationLabel = useUILabel("project.selectLocation");
  const addNewLocationLabel = useUILabel("project.addNewLocation");
  const numberOfIPsRequiredLabel = useUILabel("project.numberOfIPsRequired");
  const editWorkLabel = useUILabel("project.editProject");
  const createWorkLabel = useUILabel("project.createProject");
  const workInformationLabel = useUILabel("project.workInformation");
  const addressLabel = useUILabel("common.address");
  const additionalInformationLabel = useUILabel("project.additionalInformation");
  const updateLabel = useUILabel("common.update");
  const createLabel = useUILabel("common.create");

  // Additional labels for Create Job page
  const pricingDetailsLabel = useUILabel("project.pricingDetails");
  const selectSearchSkillsLabel = useUILabel("project.selectSearchSkills");
  const selectAManagerLabel = useUILabel("project.selectAManager");
  const addCertificationsIfNeededLabel = useUILabel("project.addCertificationsIfNeeded");
  const addTasksIfNeededLabel = useUILabel("project.addTasksIfNeeded");
  const finalStepAdditionalInformationLabel = useUILabel("project.finalStepAdditionalInformation");
  const whereIsWorkSiteLabel = useUILabel("project.whereIsWorkSite");
  const enterSOWLabel = useUILabel("project.enterSOW");
  const sowDescriptionLabel = useUILabel("project.sowDescription");
  const dateTimeInstructionsLabel = useUILabel("project.dateTimeInstructions");
  const startManagingTimeLabel = useUILabel("project.startManagingTime");
  const selectTimezoneOfWorkLabel = useUILabel("project.selectTimezoneOfWork");
  const bulkUploadTitleLabel = useUILabel("project.bulkUploadTitle");
  const templateTitleLabel = useUILabel("project.templateTitle");
  const templateInstructionsLabel = useUILabel("project.templateInstructions");
  const selectTemplateLabel = useUILabel("project.selectTemplate");
  const loadingTemplateLabel = useUILabel("project.loadingTemplate");
  const customizeLayoutLabel = useUILabel("project.customizeLayout");
  const assignWorkManagerForWorkLabel = useUILabel("project.assignWorkManagerForWork");
  const selectWorkManagerLabel = useUILabel("project.selectWorkManager");
  const whatSkillsLookingForLabel = useUILabel("project.whatSkillsLookingFor");
  const requiredCertificationsOptionalLabel = useUILabel("project.requiredCertificationsOptional");
  const selectCertificationsLabel = useUILabel("project.selectCertifications");
  const tasksOptionalNoteLabel = useUILabel("project.tasksOptionalNote");
  const addTaskLabel = useUILabel("project.addTask");
  const typeHourlyRateLabel = useUILabel("project.typeHourlyRate");
  const enterFixedRateLabel = useUILabel("project.enterFixedRate");
  const estimatedAmountOfHoursLabel = useUILabel("project.estimatedAmountOfHours");
  const worksiteRadiusMilesLabel = useUILabel("project.worksiteRadiusMiles");
  const onlyAvailableToIPsWithinRadiusLabel = useUILabel("project.onlyAvailableToIPsWithinRadius");
  const privateOnlyInvitedIPsLabel = useUILabel("project.privateOnlyInvitedIPs");
  const publicAllIPsLabel = useUILabel("project.publicAllIPs");
  const photosRequiredNoteLabel = useUILabel("project.photosRequiredNote");
  const finalNotesRequiredNoteLabel = useUILabel("project.finalNotesRequiredNote");
  const toolsRequiredNoteLabel = useUILabel("project.toolsRequiredNote");
  const backgroundCheckRequiredFormLabel = useUILabel("project.backgroundCheckRequiredLabel");
  const drugTestRequiredFormLabel = useUILabel("project.drugTestRequiredLabel");
  const confidentialInformationNoteLabel = useUILabel("project.confidentialInformationNote");
  const goToLabel = useUILabel("project.goTo");
  const workIdWorkTitleLabel = useUILabel("project.workIdWorkTitle");
  const bulkUploadWorkLabel = useUILabel("project.bulkUploadWork");

  // Error and validation messages
  const thisFieldIsRequiredLabel = useUILabel("common.thisFieldIsRequired");
  const locationIsRequiredLabel = useUILabel("common.locationIsRequired");
  const managerIsRequiredLabel = useUILabel("common.managerIsRequired");
  const invalidLabel = useUILabel("common.invalid");
  const pleaseSetValidDateAndTimeLabel = useUILabel("common.pleaseSetValidDateAndTime");

  // Success messages
  const workCreatedLabel = useUILabel("common.workCreated");
  const workUpdatedLabel = useUILabel("common.workUpdated");
  const layoutPreferencesSavedLabel = useUILabel("common.layoutPreferencesSaved");
  const failedToSavePreferencesLabel = useUILabel("common.failedToSavePreferences");

  // Pricing options
  const hourlyRateLabel = useUILabel("project.hourlyRate");
  const fixedRateLabel = useUILabel("project.fixedRateLabel");

  // Pricing options array - must be inside component to use hooks
  const pricingOptions = React.useMemo(() => [
    { value: "hourly", label: hourlyRateLabel },
    { value: "fixed", label: fixedRateLabel },
  ], [hourlyRateLabel, fixedRateLabel]);

  const [scrollToBasic, basicRef] = useScroll();
  const [scrollToSchedule, scheduleRef] = useScroll();
  const [scrollToLocation, locationRef] = useScroll();
  const [scrollToSkill, skillRef] = useScroll();
  const [scrollToPricing, pricingRef] = useScroll();
  const [scrollToMore, moreRef] = useScroll();
  const [scrollToContact, contactRef] = useScroll();
  const [scrollToCertification, certificationRef] = useScroll();
  const [scrollToDocument, documentRef] = useScroll();
  const [scrollToTask, taskRef] = useScroll();

  const [useTemplate, setUseTemplate] = React.useState(false);
  const [selectedTemplate, setSelectedTemplate] = React.useState(0);

  const [timeError, setTimeError] = React.useState<string | null>("");

  const [taskModal, setTaskModal] = React.useState<{
    open: boolean;
    data: ProjectTask | null;
  }>({
    open: false,
    data: null,
  });
  const [newTasks, setNewTasks] = React.useState<Array<ProjectTask>>([]);
  const [locationModal, setLocationModal] = React.useState<{
    open: boolean;
    data: Location | null;
  }>({
    open: false,
    data: null,
  });
  const [clientModal, setClientModal] = React.useState<{
    open: boolean;
    data: Customer | null;
  }>({
    open: false,
    data: null,
  });

  const [data, setData] = React.useState<Partial<Project> | undefined>(
    undefined
  );
  const { data: job, isLoading } = useGetProjectByIdQuery(
    parseInt(projectId!),
    {
      skip: !projectId,
    }
  );
  const {
    data: template,
    isLoading: loadingTemplate,
    isFetching: fetchingTemplate,
  } = useGetTemplateByIdQuery(selectedTemplate, {
    skip: !selectedTemplate,
  });
  const [add, { isLoading: adding }] = useAddProjectMutation();
  const [edit, { isLoading: updating }] = useEditProjectMutation();
  const [target, setTarget] = React.useState("location");
  const [addTask, { isLoading: addingTask }] = useAddProjectTaskMutation();
  const [deleteTask, { isLoading: deletingTask }] =
    useDeleteProjectTaskMutation();
  const [customizeLayoutModalOpen, setCustomizeLayoutModalOpen] =
    React.useState(false);

  // Load card preferences for project edit page
  const { data: cardPreferences } = useGetCardPreferencesQuery({
    type: CARD_PREFERENCE_TYPE.CUSTOMER,
    page: CARD_PREFERENCE_PAGE.PROJECT_EDIT,
  });
  const [updateCardPreferences] = useUpdateCardPreferencesMutation();

  // Get ordered and visible cards (excluding sub-cards)
  const cardOrder = React.useMemo(() => {
    const subCardIds = [
      PROJECT_EDIT_CARD_IDS.workSite,
      PROJECT_EDIT_CARD_IDS.startDateTime,
      PROJECT_EDIT_CARD_IDS.sow,
    ];
    if (!cardPreferences?.cardOrder || cardPreferences.cardOrder.length === 0) {
      return DEFAULT_PROJECT_EDIT_CARD_ORDER.filter(
        (id) => !subCardIds.includes(id as any)
      );
    }
    // Filter to only include valid project edit card IDs and exclude sub-cards
    const filteredOrder = cardPreferences.cardOrder.filter(
      (id) =>
        Object.values(PROJECT_EDIT_CARD_IDS).includes(id as any) &&
        !subCardIds.includes(id as any)
    );
    // Add any missing cards from default order (excluding sub-cards)
    const mainCardIds = DEFAULT_PROJECT_EDIT_CARD_ORDER.filter(
      (id) => !subCardIds.includes(id as any)
    );
    const missingCards = mainCardIds.filter(
      (id) => !filteredOrder.includes(id)
    );
    return [...filteredOrder, ...missingCards];
  }, [cardPreferences]);

  const hiddenCards = React.useMemo(() => {
    const requiredCardIds = [
      PROJECT_EDIT_CARD_IDS.workInformation,
      PROJECT_EDIT_CARD_IDS.pricingDetails,
      PROJECT_EDIT_CARD_IDS.skills,
      PROJECT_EDIT_CARD_IDS.manager,
    ];
    // Filter out required cards from hiddenCards
    return (cardPreferences?.hiddenCards || []).filter(
      (id) => !requiredCardIds.includes(id as any)
    );
  }, [cardPreferences]);

  const handleSaveCardPreferences = async (preferences: {
    cardOrder: string[];
    hiddenCards: string[];
    workInformationSubCardsOrder: string[];
    hiddenWorkInformationSubCards: string[];
  }) => {
    try {
      const requiredCardIds = [
        PROJECT_EDIT_CARD_IDS.workInformation,
        PROJECT_EDIT_CARD_IDS.pricingDetails,
        PROJECT_EDIT_CARD_IDS.skills,
        PROJECT_EDIT_CARD_IDS.manager,
      ];
      const requiredSubCardIds = [
        PROJECT_EDIT_CARD_IDS.workSite,
        PROJECT_EDIT_CARD_IDS.startDateTime,
        PROJECT_EDIT_CARD_IDS.sow,
      ];
      // Filter out required cards from hiddenCards before saving
      const filteredHiddenCards = preferences.hiddenCards.filter(
        (id) => !requiredCardIds.includes(id as any)
      );
      // Filter out required sub-cards from hiddenWorkInformationSubCards before saving
      const filteredHiddenSubCards = preferences.hiddenWorkInformationSubCards.filter(
        (id) => !requiredSubCardIds.includes(id as any)
      );
      await updateCardPreferences({
        type: CARD_PREFERENCE_TYPE.CUSTOMER,
        page: CARD_PREFERENCE_PAGE.PROJECT_EDIT,
        data: {
          cardOrder: preferences.cardOrder,
          hiddenCards: filteredHiddenCards,
          userId: user?.id,
          details: {
            workInformation: {
              workInformationSubCardsOrder: preferences.workInformationSubCardsOrder,
              hiddenWorkInformationSubCards: filteredHiddenSubCards,
            },
          },
        },
      }).unwrap();
      toast.success(layoutPreferencesSavedLabel);
    } catch (error: any) {
      toast.error(error?.data?.message || failedToSavePreferencesLabel);
      throw error;
    }
  };

  // Helper function to check if section should be rendered
  const shouldRenderSection = React.useCallback((cardId: string) => {
    return cardOrder.includes(cardId) && !hiddenCards.includes(cardId);
  }, [cardOrder, hiddenCards]);

  // Get sub-cards order for Work Information from details.workInformation
  const workInformationSubCardsOrder = React.useMemo(() => {
    const subCardIds = [
      PROJECT_EDIT_CARD_IDS.workSite,
      PROJECT_EDIT_CARD_IDS.startDateTime,
      PROJECT_EDIT_CARD_IDS.sow,
    ];
    const savedOrder =
      cardPreferences?.details?.workInformation?.workInformationSubCardsOrder ||
      subCardIds;
    const validOrder = savedOrder.filter((id: string) =>
      subCardIds.includes(id as any)
    );
    const missing = subCardIds.filter((id: string) => !validOrder.includes(id));
    return [...validOrder, ...missing];
  }, [cardPreferences?.details?.workInformation?.workInformationSubCardsOrder]);

  const hiddenWorkInformationSubCards = React.useMemo(() => {
    const requiredSubCardIds = [
      PROJECT_EDIT_CARD_IDS.workSite,
      PROJECT_EDIT_CARD_IDS.startDateTime,
      PROJECT_EDIT_CARD_IDS.sow,
    ];
    // Filter out required sub-cards from hiddenWorkInformationSubCards
    return (
      cardPreferences?.details?.workInformation?.hiddenWorkInformationSubCards ||
      []
    ).filter((id: string) => !requiredSubCardIds.includes(id as any));
  }, [cardPreferences?.details?.workInformation?.hiddenWorkInformationSubCards]);

  // Mapping of card IDs to their scroll refs
  const cardRefs = React.useMemo(() => ({
    [PROJECT_EDIT_CARD_IDS.workInformation]: basicRef,
    [PROJECT_EDIT_CARD_IDS.pricingDetails]: pricingRef,
    [PROJECT_EDIT_CARD_IDS.skills]: skillRef,
    [PROJECT_EDIT_CARD_IDS.certifications]: certificationRef,
    [PROJECT_EDIT_CARD_IDS.manager]: contactRef,
    [PROJECT_EDIT_CARD_IDS.tasks]: taskRef,
    [PROJECT_EDIT_CARD_IDS.additionalInformation]: moreRef,
  }), [basicRef, pricingRef, skillRef, certificationRef, contactRef, taskRef, moreRef]);

  const isEdit = React.useMemo(() => !!projectId, [projectId]);
  useTitle(!!isEdit ? editWorkLabel : createWorkLabel);

  const {
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    trigger,
    formState: { errors },
    register,
    unregister,
    watch,
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  const watchType = watch("type");

  // Register manager_id to ensure it's included in form data
  React.useEffect(() => {
    register("manager_id", { required: true });
  }, [register]);

  React.useEffect(() => {
    if (watchType === "hourly") {
      register("hourly_rate", { required: true });
      unregister("total_pay");
    } else {
      register("total_pay", { required: true });
      unregister("hourly_rate");
    }
  }, [register, unregister, watchType]);

  const onSubmit = async (payload: any) => {
    try {
      if (target === "customer" && !payload.customer) {
        setError("customer", { type: "custom", message: invalidLabel });
        return;
      } else if (target === "location" && !payload.location) {
        setError("location", { type: "custom", message: locationIsRequiredLabel });
        (scrollToLocation as any)();
        return;
      }
      if (!payload.start_date) {
        setError("start_date", { type: "required", message: thisFieldIsRequiredLabel });
        await trigger("start_date");
        return;
      }
      if (!payload.timezone) {
        setError("timezone", { type: "required", message: thisFieldIsRequiredLabel });
        await trigger("timezone");
        return;
      }
      if (target === "customer") payload.location = undefined;
      else payload.customer = undefined;

      // Ensure manager is included in payload (backend expects "manager" not "manager_id")
      // Get manager_id from form values if not in payload
      const managerId = payload.manager_id || watch("manager_id");
      if (!managerId) {
        setError("manager_id", { type: "custom", message: managerIsRequiredLabel });
        (scrollToContact as any)();
        return;
      }
      // Backend expects "manager" field, not "manager_id"
      payload.manager = managerId;
      // Remove manager_id from payload to avoid confusion
      delete payload.manager_id;

      //payload.start_date = dayjs(payload.start_date).format("YYYY-MM-DD HH:mm");

      // IF BACKEND STORES UTC
      // payload.start_date = dayjs(payload.start_date)
      //   .tz(watch("timezone") || myTimezone, true)
      //   .format("YYYY-MM-DD HH:mm");

      // payload.start_date = dayjs(payload.start_date, "YYYY-MM-DD HH:mm")
      //   .second(0)
      //   .format("YYYY-MM-DD HH:mm");

      // payload.start_date = dayjs(payload.start_date)
      //   .utc(true)
      //   .format("YYYY-MM-DD HH:mm");

      // payload.start_date = dayjs(payload.start_date)
      //   .millisecond(0)
      //   .format("YYYY-MM-DD HH:mm");

      // payload.start_date = dayjs(payload.start_date)
      //   .millisecond(0)
      //   .format("YYYY-MM-DD HH:mm");
      // payload.start_date = dayjs(payload.start_date, "YYYY-MM-DD HH:mm").format(
      //   "YYYY-MM-DD HH:mm"
      // );
      // payload.start_date = dayjs(payload.start_date).format("YYYY-MM-DD HH:mm");

      payload.start_date = dayjs(payload.start_date).format(
        "YYYY-MM-DD HH:mm"
      );

      if (!job) {
        const project: Project = await add(payload).unwrap();

        if (newTasks.length > 0) {
          let promises = newTasks
            .filter((it) => it._internal_op === "add")
            .map(
              async (it) =>
                await addTask({
                  name: it.name,
                  project: Number(project.id),
                  description: it.description || undefined,
                  // start_date: !!it.start_date
                  //   ? dayjs(it.start_date).format("YYYY-MM-DD HH:mm")
                  //   : undefined,
                })
            );
          await Promise.all(promises);
        }
        toast.success(workCreatedLabel);
        navigate(`/works/${project.id}`, { replace: true });
        //
      } else {
        if (!payload.password) delete payload.password;
        payload.id = job.id;
        // console.log("payload", payload);

        await edit(payload).unwrap();

        if (newTasks.length > 0) {
          let promises = newTasks.map(async (it) => {
            if (it._internal_op === "add") {
              return await addTask({
                name: it.name,
                project: Number(projectId),
                description: it.description || undefined,
                // start_date: !!it.start_date
                //   ? dayjs(it.start_date).format("YYYY-MM-DD HH:mm")
                //   : undefined,
              });
            } else if (it._internal_op === "delete") {
              return await deleteTask({
                id: it.id,
                project: Number(projectId),
              });
            }
          });
          await Promise.all(promises);
        }

        toast.success(workUpdatedLabel);
        navigate(`/works/${job.id}`, { replace: true });
      }
    } catch (err: any) {
      toast.error(err);
    }
  };
  // ProjectStatus.DRAFT,
  // ProjectStatus.PUBLISHED,
  // ProjectStatus.IN_PROGRESS,

  const StickyMenu = React.useCallback(
    () => (
      <StickyBox offsetTop={70} offsetBottom={20}>
        {job?.status === ProjectStatus.SCHEDULED ||
          job?.status === ProjectStatus.IN_PROGRESS ? (
          <CardBase
            useGlassCard
            glassEffect="clear"
            sx={{
              p: 0,
              "& .MuiCardContent-root": {
                p: { xs: 1.5, sm: 2.5 },
              },
            }}
          >
            <StyledNavigationList disablePadding>
              <ListItem disablePadding>
                <StyledNavigationButton onClick={() => (scrollToContact as any)()}>
                  <ListItemText
                    primary={selectAManagerLabel}
                    primaryTypographyProps={{
                      fontSize: { xs: "0.875rem", sm: "0.95rem" },
                    }}
                  />
                </StyledNavigationButton>
              </ListItem>
            </StyledNavigationList>
          </CardBase>
        ) : (
          <CardBase
            title={goToLabel}
            useGlassCard
            glassEffect="clear"
            sx={(theme) => ({
              p: 0,
              "& .MuiCardHeader-root": {
                px: { xs: 2, sm: 3 },
                pb: { xs: 0.5, sm: 1 },
              },
              "& .MuiTypography-h6": {
                fontWeight: 700,
                fontSize: { xs: "0.95rem", sm: "1.05rem" },
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                letterSpacing: "0.5px",
              },
              "& .MuiCardContent-root": {
                pt: 0.5,
                pb: { xs: 1.5, sm: 2.5 },
                px: { xs: 1.5, sm: 2.5 },
              },
            })}
          >
            <StyledNavigationList disablePadding>
              <ListItem disablePadding>
                <StyledNavigationButton onClick={() => (scrollToBasic as any)()}>
                  <ListItemText
                    primary={workInformationLabel}
                    primaryTypographyProps={{
                      fontSize: { xs: "0.875rem", sm: "0.95rem" },
                    }}
                  />
                </StyledNavigationButton>
              </ListItem>
              <ListItem disablePadding>
                <StyledNavigationButton onClick={() => (scrollToPricing as any)()}>
                  <ListItemText
                    primary={pricingDetailsLabel}
                    primaryTypographyProps={{
                      fontSize: { xs: "0.875rem", sm: "0.95rem" },
                    }}
                  />
                </StyledNavigationButton>
              </ListItem>
              <ListItem disablePadding>
                <StyledNavigationButton onClick={() => (scrollToSkill as any)()}>
                  <ListItemText
                    primary={selectSearchSkillsLabel}
                    primaryTypographyProps={{
                      fontSize: { xs: "0.875rem", sm: "0.95rem" },
                    }}
                  />
                </StyledNavigationButton>
              </ListItem>
              <ListItem disablePadding>
                <StyledNavigationButton onClick={() => (scrollToContact as any)()}>
                  <ListItemText
                    primary={selectAManagerLabel}
                    primaryTypographyProps={{
                      fontSize: { xs: "0.875rem", sm: "0.95rem" },
                    }}
                  />
                </StyledNavigationButton>
              </ListItem>
              <ListItem disablePadding>
                <StyledNavigationButton onClick={() => (scrollToCertification as any)()}>
                  <ListItemText
                    primary={addCertificationsIfNeededLabel}
                    primaryTypographyProps={{
                      fontSize: { xs: "0.875rem", sm: "0.95rem" },
                    }}
                  />
                </StyledNavigationButton>
              </ListItem>
              <ListItem disablePadding>
                <StyledNavigationButton onClick={() => (scrollToTask as any)()}>
                  <ListItemText
                    primary={addTasksIfNeededLabel}
                    primaryTypographyProps={{
                      fontSize: { xs: "0.875rem", sm: "0.95rem" },
                    }}
                  />
                </StyledNavigationButton>
              </ListItem>
              <ListItem disablePadding>
                <StyledNavigationButton onClick={() => (scrollToMore as any)()}>
                  <ListItemText
                    primary={finalStepAdditionalInformationLabel}
                    primaryTypographyProps={{
                      fontSize: { xs: "0.875rem", sm: "0.95rem" },
                    }}
                  />
                </StyledNavigationButton>
              </ListItem>
            </StyledNavigationList>
          </CardBase>
        )}
      </StickyBox>
    ),
    [job?.status, scrollToBasic, scrollToPricing, scrollToSkill, scrollToContact, scrollToCertification, scrollToTask, scrollToMore]
  );

  const handleAddTask = (task: ProjectTask) => {
    task.id = Date.now();
    task._internal_op = "add";
    setTaskModal({ open: false, data: null });
    setNewTasks([...newTasks, task]);
  };
  const handleDeleteTask = (task: ProjectTask) => {
    const newList = newTasks
      .filter((it) => it.id !== task.id || it._internal_op !== "add")
      .map((it) => {
        if (it.id === task.id) {
          return {
            ...it,
            _internal_op: "delete" as any,
          };
        }
        return it;
      });
    setNewTasks(newList);
  };

  React.useEffect(() => {
    if (!!template) setData(template);
    else setData(job);
  }, [job, template]);

  React.useEffect(() => {
    setValue("title", decode(data?.title) || "");
    setValue("description", decode(data?.description) || "");
    // setValue("start_date", data?.start_date || null);
    //setValue("start_date", data?.start_date ? dayjs(data.start_date) : null);
    // setValue(
    //   "start_date",
    //   data?.start_date ? dayjs.utc(data.start_date) : null
    // );
    //setValue("start_date", data?.start_date ? dayjs(data.start_date) : null);
    // setValue(
    //   "start_date",
    //   data?.start_date ? dayjs(data.start_date).startOf("minute") : null
    // );
    // setValue(
    //   "start_date",
    //   data?.start_date ? dayjs(data.start_date).utc(true).local() : null
    // );
    // setValue(
    //   "start_date",
    //   data?.start_date ? dayjs(data.start_date).millisecond(0) : null
    // );
    // setValue(
    //   "start_date",
    //   data?.start_date
    //     ? dayjs
    //         .tz(data.start_date, watch("timezone") || myTimezone)
    //         .format("YYYY-MM-DD HH:mm")
    //     : null
    // );
    // setValue(
    //   "start_date",
    //   data?.start_date
    //     ? dayjs
    //         .tz(data.start_date, data.timezone || myTimezone)
    //         .format("YYYY-MM-DD HH:mm")
    //     : null
    // );
    setValue("start_date", data?.start_date ? dayjs(data.start_date) : null);

    setValue(
      "confidential_information",
      decode(data?.confidential_information) || ""
    );
    setValue("is_private", data?.is_private || 0);
    setValue(
      "approximate_hours_to_complete",
      data?.approximate_hours_to_complete || ""
    );
    setValue("location", data?.location_id);
    setValue("manager_id", data?.manager_id);
    setValue("customer", data?.customer_id);

    if (!!data?.customer_id) {
      setTarget("customer");
    } else {
      // default on init
      setTarget("location");
    }

    const skillIds = data?.skills?.map((it) => it.id);
    setValue("skills", skillIds || []);

    const certIds = data?.certifications?.map((it) => it.id);
    setValue("certifications", certIds || []);

    setNewTasks(data?.tasks || []);

    setValue("is_photo_required", !!data?.is_photo_required || false);
    setValue(
      "is_final_notes_required",
      !!data?.is_final_notes_required || false
    );
    setValue("tool_required", !!data?.tool_required || false);
    // setValue("covid_vaccine_required", !!data?.covid_vaccine_required || false);
    setValue(
      "background_check_required",
      !!data?.background_check_required || false
    );
    setValue("drug_test_required", !!data?.drug_test_required || false);
    setValue("mile_radius", data?.mile_radius);
    setValue("type", data?.type || "hourly");
    setValue("hourly_rate", data?.hourly_rate || null);
    setValue("total_pay", data?.total_pay || 0);
    setValue("num_of_talent", data?.num_of_talent || 1);
    setValue("internal_notes", data?.internal_notes);
    setValue("timezone", data?.timezone || myTimezone);
  }, [data]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDateMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        pb: { xs: 6, sm: 6 },
        px: { xs: 0.75, sm: 0 },
        minHeight: "100vh",
        width: "100%",
        // Remove all padding for mobile landscape, keep padding for portrait
        "@media (orientation: landscape) and (max-width: 600px)": {
          px: 0,
          mx: 0,
        },
      }}
    >
      <Grid
        container
        spacing={{ xs: 1, sm: 3 }}
        sx={{
          "@media (orientation: landscape) and (max-width: 600px)": {
            mx: 0,
            width: "100%",
            "& > .MuiGrid-item": {
              px: 0.25,
            },
          },
        }}
      >
        {!isMobile && (
          <Grid item xs={12} md={3}>
            <StickyMenu />
          </Grid>
        )}
        <Grid item xs={12} md={isMobile ? 12 : 9}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ pb: { xs: 6, sm: 8 } }}>
              {!isEdit && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      width: "100%",
                      mb: { xs: 1, sm: 1.5 },
                      mt: { xs: 2, sm: 3 },
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<Settings />}
                      onClick={() => setCustomizeLayoutModalOpen(true)}
                      sx={{
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        borderRadius: "12px",
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      {customizeLayoutLabel}
                    </Button>
                  </Box>
                  {cardOrder.map((cardId) => {
                    // Skip sub-cards of Work Information (they are rendered inside Work Information card)
                    if (
                      cardId === PROJECT_EDIT_CARD_IDS.workSite ||
                      cardId === PROJECT_EDIT_CARD_IDS.startDateTime ||
                      cardId === PROJECT_EDIT_CARD_IDS.sow
                    ) {
                      return null;
                    }
                    // Only render bulkUpload and template when !isEdit
                    if (cardId !== PROJECT_EDIT_CARD_IDS.bulkUpload && cardId !== PROJECT_EDIT_CARD_IDS.template) {
                      return null;
                    }
                    if (!shouldRenderSection(cardId)) return null;

                    // Bulk Upload Card
                    if (cardId === PROJECT_EDIT_CARD_IDS.bulkUpload) {
                      return (
                        <CardBase
                          key={cardId}
                          title={bulkUploadTitleLabel}
                          useGlassCard={true}
                          glassEffect="clear"
                          sx={{ mt: 0 }}
                        >
                          <Box sx={{
                            px: { xs: 1, sm: 1.5 },
                            py: { xs: 2, sm: 3 },
                            "@media (orientation: landscape) and (max-width: 600px)": {
                              px: 0.5,
                              py: 1.5,
                            },
                          }}>
                            <Button
                              color="secondary"
                              variant="outlined"
                              startIcon={<UploadFileOutlined />}
                              onClick={() => navigate("/works/create/bulk-upload")}
                              fullWidth={isMobile}
                              sx={{
                                fontSize: { xs: "0.875rem", sm: "1rem" },
                              }}
                            >
                              {bulkUploadWorkLabel}
                            </Button>
                          </Box>
                        </CardBase>
                      );
                    }

                    // Template Card
                    if (cardId === PROJECT_EDIT_CARD_IDS.template) {
                      return (
                        <CardBase
                          key={cardId}
                          title={templateTitleLabel}
                          useGlassCard
                          glassEffect="clear"
                          sx={{ mt: { xs: 2, sm: 3 } }}
                        >
                          <Box sx={{
                            px: { xs: 1, sm: 1.5 },
                            py: { xs: 2, sm: 3 },
                            "@media (orientation: landscape) and (max-width: 600px)": {
                              px: 0.5,
                              py: 1.5,
                            },
                          }}>
                            <Stack>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={useTemplate}
                                    onChange={(e) => setUseTemplate(e.target.checked)}
                                  />
                                }
                                label={templateInstructionsLabel}
                                sx={{
                                  "& .MuiFormControlLabel-label": {
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                  },
                                }}
                              />
                            </Stack>
                            {useTemplate && (
                              <SelectTemplate
                                label={selectTemplateLabel}
                                onChange={(id) => {
                                  if (!!id) setSelectedTemplate(id);
                                }}
                                value={selectedTemplate}
                                sx={{ mt: 1 }}
                                error={!!errors.customer}
                                helperText={
                                  !!errors.customer && thisFieldIsRequiredLabel
                                }
                              />
                            )}
                            {(loadingTemplate || fetchingTemplate) && (
                              <Box>
                                <Typography variant="caption">
                                  {loadingTemplateLabel}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </CardBase>
                      );
                    }

                    return null;
                  })}
                </>
              )}
              {job?.status === ProjectStatus.SCHEDULED ||
                job?.status === ProjectStatus.IN_PROGRESS ? (
                <>
                  <CardBase
                    title={assignWorkManagerForWorkLabel}
                    useGlassCard
                    glassEffect="clear"
                    sx={{ mt: 3 }}
                    ref={contactRef}
                  >
                    <Box sx={{ px: { xs: 1, sm: 1.5 }, py: { xs: 2, sm: 3 } }}>
                      <Box>
                        <Controller
                          name="manager_id"
                          control={control}
                          rules={{ required: true }}
                          render={({ field: { onChange, value } }) => (
                            <SelectManager
                              label={selectWorkManagerLabel}
                              onChange={onChange}
                              value={value}
                              error={!!errors.manager_id}
                              helperText={
                                !!errors.manager_id && thisFieldIsRequiredLabel
                              }
                            />
                          )}
                        />
                      </Box>
                    </Box>
                  </CardBase>
                </>
              ) : (
                <>
                  {cardOrder.map((cardId) => {
                    // Skip bulkUpload and template in edit mode
                    if (cardId === PROJECT_EDIT_CARD_IDS.bulkUpload || cardId === PROJECT_EDIT_CARD_IDS.template) {
                      return null;
                    }
                    // Skip sub-cards of Work Information (they are rendered inside Work Information card)
                    if (
                      cardId === PROJECT_EDIT_CARD_IDS.workSite ||
                      cardId === PROJECT_EDIT_CARD_IDS.startDateTime ||
                      cardId === PROJECT_EDIT_CARD_IDS.sow
                    ) {
                      return null;
                    }
                    if (!shouldRenderSection(cardId)) return null;

                    // Work Information Card
                    if (cardId === PROJECT_EDIT_CARD_IDS.workInformation) {
                      return (
                        <CardBase
                          key={cardId}
                          title={workInformationLabel}
                          useGlassCard
                          glassEffect="clear"
                          sx={{ mt: { xs: 2, sm: 3 } }}
                          ref={cardRefs[cardId]}
                        >
                          <Box sx={{
                            px: { xs: 1, sm: 1.5 },
                            py: { xs: 2, sm: 3 },
                            "@media (orientation: landscape) and (max-width: 600px)": {
                              px: 0.5,
                              py: 1.5,
                            },
                          }}>
                            <Controller
                              name="title"
                              control={control}
                              defaultValue=""
                              rules={{ required: true }}
                              render={({
                                field: { onChange, onBlur, value, ref },
                              }) => (
                                <StyledTextField
                                  InputLabelProps={{ shrink: !!value }}
                                  label={workIdWorkTitleLabel}
                                  value={value || ""}
                                  fullWidth
                                  onChange={onChange}
                                  onBlur={onBlur}
                                  error={!!errors.title}
                                  helperText={
                                    !!errors.title && thisFieldIsRequiredLabel
                                  }
                                />
                              )}
                            />
                            {workInformationSubCardsOrder
                              .filter(
                                (subCardId) =>
                                  !hiddenWorkInformationSubCards.includes(subCardId)
                              )
                              .map((subCardId) => {

                                // Work Site Sub-Card
                                if (subCardId === PROJECT_EDIT_CARD_IDS.workSite) {
                                  return (
                                    <CardBase
                                      key={subCardId}
                                      title={whereIsWorkSiteLabel}
                                      useGlassCard
                                      glassEffect="clear"
                                      nested
                                      sx={{ mt: { xs: 2, sm: 3 } }}
                                      ref={locationRef}
                                    >
                                      <Box sx={{
                                        px: { xs: 1, sm: 1.5 },
                                        py: { xs: 2, sm: 3 },
                                        "@media (orientation: landscape) and (max-width: 600px)": {
                                          px: 0.5,
                                          py: 1.5,
                                        },
                                      }}>
                                        {target === "location" && (
                                          <>
                                            <Controller
                                              name="location"
                                              control={control}
                                              rules={{ required: true }}
                                              render={({ field: { onChange, value } }) => (
                                                <SelectLocation
                                                  label={selectLocationLabel}
                                                  onChange={onChange}
                                                  value={value}
                                                  sx={{ mt: 0 }}
                                                  error={!!errors.location}
                                                  helperText={
                                                    !!errors.location &&
                                                    thisFieldIsRequiredLabel
                                                  }
                                                />
                                              )}
                                            />
                                            <Button
                                              variant="text"
                                              onClick={() =>
                                                setLocationModal({ open: true, data: null })
                                              }
                                              sx={{ mt: 2 }}
                                            >
                                              {addNewLocationLabel}
                                            </Button>
                                          </>
                                        )}
                                        {target === "new" && (
                                          <Controller
                                            name="address"
                                            control={control}
                                            rules={{ required: true }}
                                            render={({
                                              field: { onChange, onBlur, value, ref },
                                            }) => (
                                              <StyledTextField
                                                sx={{ mt: 2 }}
                                                InputLabelProps={{ style: { fontSize: 14 } }}
                                                label={addressLabel}
                                                value={value || ""}
                                                fullWidth
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                error={!!errors.address}
                                                helperText={
                                                  !!errors.address && thisFieldIsRequiredLabel
                                                }
                                              />
                                            )}
                                          />
                                        )}
                                      </Box>
                                    </CardBase>
                                  );
                                }

                                // Start Date Time Sub-Card
                                if (subCardId === PROJECT_EDIT_CARD_IDS.startDateTime) {
                                  return (
                                    <CardBase
                                      key={subCardId}
                                      title={dateTimeInstructionsLabel}
                                      useGlassCard
                                      glassEffect="clear"
                                      nested
                                      sx={{ mt: { xs: 2, sm: 3 } }}
                                    >
                                      <Box sx={{
                                        px: { xs: 1, sm: 1.5 },
                                        py: { xs: 2, sm: 3 },
                                        "@media (orientation: landscape) and (max-width: 600px)": {
                                          px: 0.5,
                                          py: 1.5,
                                        },
                                      }}>
                                        <Controller
                                          name="start_date"
                                          control={control}
                                          rules={{ required: true }}
                                          render={({
                                            field: { onChange, onBlur, value, ref },
                                          }) => {
                                            const pickerSlotProps = {
                                              textField: {
                                                error: !!errors.start_date,
                                                helperText:
                                                  !!errors.start_date &&
                                                  (errors.start_date.message === invalidLabel
                                                    ? pleaseSetValidDateAndTimeLabel
                                                    : thisFieldIsRequiredLabel),
                                                sx: {
                                                  "& .MuiOutlinedInput-root": {
                                                    borderRadius: "12px !important",
                                                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                                    position: "relative",
                                                    minHeight: { xs: "46px", sm: "52px" },
                                                    padding: { xs: "6px 10px", sm: "10px 16px" },
                                                    "@media (orientation: landscape) and (max-width: 900px)": {
                                                      borderRadius: "8px !important",
                                                      minHeight: "32px",
                                                      padding: "4px 8px",
                                                    },
                                                    background: theme.palette.mode === "dark"
                                                      ? "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 50%, rgba(18,144,60,0.05) 100%)"
                                                      : "linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 50%, rgba(18,144,60,0.08) 100%)",
                                                    backdropFilter: "blur(40px) saturate(180%)",
                                                    WebkitBackdropFilter: "blur(40px) saturate(180%)",
                                                    border: `1.5px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.4)" : "rgba(255, 255, 255, 0.8)"}`,
                                                    boxShadow: theme.palette.mode === "dark"
                                                      ? "0 8px 32px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                                                      : "0 8px 32px rgba(18, 144, 60, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
                                                    overflow: "hidden",
                                                    "&::before": {
                                                      content: '""',
                                                      position: "absolute",
                                                      top: 0,
                                                      left: 0,
                                                      right: 0,
                                                      height: "50%",
                                                      background: `linear-gradient(180deg, ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(255, 255, 255, 0.25)"} 0%, transparent 100%)`,
                                                      pointerEvents: "none",
                                                      zIndex: 0,
                                                    },
                                                    "&::after": {
                                                      content: '""',
                                                      position: "absolute",
                                                      top: 0,
                                                      left: 0,
                                                      right: 0,
                                                      bottom: 0,
                                                      background: `linear-gradient(135deg, transparent 0%, rgba(18,144,60,0.03) 100%)`,
                                                      pointerEvents: "none",
                                                      zIndex: 0,
                                                    },
                                                    "&:hover": {
                                                      transform: "translateY(-2px)",
                                                      boxShadow: theme.palette.mode === "dark"
                                                        ? "0 12px 48px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.4)"
                                                        : "0 12px 48px rgba(18, 144, 60, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.7)",
                                                      border: `1.5px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(255, 255, 255, 0.9)"}`,
                                                      background: theme.palette.mode === "dark"
                                                        ? "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.1) 50%, rgba(18,144,60,0.06) 100%)"
                                                        : "linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.18) 50%, rgba(18,144,60,0.1) 100%)",
                                                    },
                                                    "&.Mui-focused": {
                                                      transform: "translateY(-2px)",
                                                      boxShadow: `0 12px 48px ${theme.palette.primary.main}33, inset 0 1px 0 rgba(255, 255, 255, 0.7)`,
                                                      border: `1.5px solid rgba(255, 255, 255, 0.95)`,
                                                      background: theme.palette.mode === "dark"
                                                        ? "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.12) 50%, rgba(18,144,60,0.08) 100%)"
                                                        : "linear-gradient(135deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.22) 50%, rgba(18,144,60,0.12) 100%)",
                                                    },
                                                    "& .MuiInputBase-input": {
                                                      position: "relative",
                                                      zIndex: 1,
                                                      fontSize: { xs: "0.875rem", sm: "1rem" },
                                                      padding: { xs: "4px 0", sm: "6px 0" },
                                                      "@media (orientation: landscape) and (max-width: 900px)": {
                                                        fontSize: "0.8125rem",
                                                        padding: "6px 0",
                                                      },
                                                    },
                                                  },
                                                  "& .MuiOutlinedInput-notchedOutline": {
                                                    borderRadius: "12px",
                                                    border: "none",
                                                    "@media (orientation: landscape) and (max-width: 900px)": {
                                                      borderRadius: "8px",
                                                    },
                                                  },
                                                  "& .MuiInputLabel-root": {
                                                    zIndex: 1,
                                                    fontSize: { xs: "0.85rem", sm: "1rem" },
                                                    "@media (orientation: landscape) and (max-width: 900px)": {
                                                      fontSize: "0.8125rem",
                                                    },
                                                  },
                                                  "& .MuiInputAdornment-root svg": {
                                                    fontSize: { xs: "1.1rem", sm: "1.25rem" },
                                                    "@media (orientation: landscape) and (max-width: 900px)": {
                                                      fontSize: "1rem",
                                                    },
                                                  },
                                                },
                                              },
                                            };

                                            const sharedProps = {
                                              sx: {
                                                mt: 2,
                                                width: { xs: "100%", sm: "80%" },
                                                "@media (orientation: landscape) and (max-width: 900px)": {
                                                  mt: 1.5,
                                                  width: "100%",
                                                },
                                              },
                                              onChange,
                                              value: value ? value : null,
                                              label: startManagingTimeLabel,
                                              format: "MM/DD/YYYY hh:mm A",
                                              onError: () => {
                                                setError("start_date", {
                                                  type: "custom",
                                                  message: invalidLabel,
                                                });
                                              },
                                              onAccept: () => clearErrors("start_date"),
                                            };

                                            if (isDateMobile) {
                                              return (
                                                <MobileDateTimePicker
                                                  {...sharedProps}
                                                  slotProps={{
                                                    ...pickerSlotProps,
                                                    mobilePaper: {
                                                      sx: {
                                                        mx: 1,
                                                        borderRadius: 3,
                                                        "& .MuiPickersToolbar-root": {
                                                          px: 2,
                                                        },
                                                        "& .MuiDialogActions-root": {
                                                          px: 2,
                                                          pb: 2,
                                                        },
                                                      },
                                                    },
                                                  }}
                                                />
                                              );
                                            }

                                            return (
                                              <DesktopDateTimePicker
                                                {...sharedProps}
                                                slotProps={{
                                                  ...pickerSlotProps,
                                                }}
                                              />
                                            );
                                          }}
                                        />
                                        <Controller
                                          name="timezone"
                                          control={control}
                                          rules={{
                                            required: thisFieldIsRequiredLabel
                                          }}
                                          render={({ field: { onChange, value } }) => (
                                            <SelectTimezone
                                              sx={{
                                                width: { xs: "100%", sm: "50%" },
                                                mt: 2,
                                                "@media (orientation: landscape) and (max-width: 900px)": {
                                                  mt: 1.5,
                                                  width: "100%",
                                                },
                                              }}
                                              label={selectTimezoneOfWorkLabel}
                                              onChange={onChange}
                                              value={value}
                                              error={!!errors.timezone}
                                              helperText={
                                                !!errors.timezone
                                                  ? (typeof errors.timezone.message === "string"
                                                    ? errors.timezone.message
                                                    : thisFieldIsRequiredLabel)
                                                  : ""
                                              }
                                            />
                                          )}
                                        />
                                      </Box>
                                    </CardBase>
                                  );
                                }

                                // SOW Sub-Card
                                if (subCardId === PROJECT_EDIT_CARD_IDS.sow) {
                                  return (
                                    <CardBase
                                      key={subCardId}
                                      title={enterSOWLabel}
                                      useGlassCard
                                      glassEffect="clear"
                                      nested
                                      sx={{ mt: { xs: 2, sm: 3 } }}
                                    >
                                      <Box sx={{
                                        px: { xs: 1, sm: 1.5 },
                                        py: { xs: 2, sm: 3 },
                                        "@media (orientation: landscape) and (max-width: 600px)": {
                                          px: 0.5,
                                          py: 1.5,
                                        },
                                      }}>
                                        <Controller
                                          name="description"
                                          control={control}
                                          defaultValue=""
                                          rules={{ required: true }}
                                          render={({
                                            field: { onChange, onBlur, value, ref },
                                          }) => (
                                            <StyledTextField
                                              sx={{ mt: 2 }}
                                              InputLabelProps={{ style: { fontSize: 14 } }}
                                              label={sowDescriptionLabel}
                                              multiline={true}
                                              minRows={10}
                                              value={value || ""}
                                              fullWidth
                                              onChange={onChange}
                                              onBlur={onBlur}
                                              error={!!errors.description}
                                              helperText={
                                                !!errors.description &&
                                                thisFieldIsRequiredLabel
                                              }
                                            />
                                          )}
                                        />
                                      </Box>
                                    </CardBase>
                                  );
                                }

                                return null;
                              })}
                            <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                              <Controller
                                name="confidential_information"
                                control={control}
                                defaultValue=""
                                render={({
                                  field: { onChange, onBlur, value, ref },
                                }) => (
                                  <StyledTextField
                                    sx={{ mt: 2 }}
                                    InputLabelProps={{ style: { fontSize: 14 } }}
                                    label={confidentialInformationNoteLabel}
                                    multiline={true}
                                    minRows={2}
                                    value={value || ""}
                                    fullWidth
                                    onChange={onChange}
                                    onBlur={onBlur}
                                  />
                                )}
                              />
                              <Controller
                                name="num_of_talent"
                                control={control}
                                defaultValue=""
                                rules={{ required: true }}
                                render={({
                                  field: { onChange, onBlur, value, ref },
                                }) => (
                                  <StyledTextField
                                    sx={{ mt: 2 }}
                                    InputLabelProps={{ shrink: !!value }}
                                    label={numberOfIPsRequiredLabel}
                                    value={value || ""}
                                    disabled={true}
                                    fullWidth
                                    type={"number"}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    error={!!errors.num_of_talent}
                                    helperText={
                                      !!errors.num_of_talent && thisFieldIsRequiredLabel
                                    }
                                  />
                                )}
                              />

                              <Controller
                                name="is_private"
                                control={control}
                                defaultValue="0"
                                render={({
                                  field: { onChange, onBlur, value, ref },
                                }) => (
                                  <RadioGroup
                                    value={value}
                                    onChange={onChange}
                                    defaultValue="0"
                                    sx={{ mt: 2 }}
                                  >
                                    <FormControlLabel
                                      value="1"
                                      control={<Radio />}
                                      label={privateOnlyInvitedIPsLabel}
                                      sx={{
                                        "& .MuiFormControlLabel-label": {
                                          fontSize: { xs: "0.875rem", sm: "1rem" },
                                        },
                                      }}
                                    />
                                    <FormControlLabel
                                      value="0"
                                      control={<Radio />}
                                      label={publicAllIPsLabel}
                                      sx={{
                                        "& .MuiFormControlLabel-label": {
                                          fontSize: { xs: "0.875rem", sm: "1rem" },
                                        },
                                      }}
                                    />
                                  </RadioGroup>
                                )}
                              />
                            </Box>
                          </Box>
                        </CardBase>
                      );
                    }

                    // Pricing Details Card
                    if (cardId === PROJECT_EDIT_CARD_IDS.pricingDetails) {
                      return (
                        <CardBase
                          key={cardId}
                          title={pricingDetailsLabel}
                          useGlassCard
                          glassEffect="clear"
                          sx={{ mt: { xs: 2, sm: 3 } }}
                          ref={cardRefs[cardId]}
                        >
                          <Box sx={{
                            px: { xs: 1, sm: 1.5 },
                            py: { xs: 2, sm: 3 },
                            "@media (orientation: landscape) and (max-width: 600px)": {
                              px: 0.5,
                              py: 1.5,
                            },
                          }}>
                            <Controller
                              name="type"
                              control={control}
                              defaultValue="hourly"
                              render={({ field: { onChange, value } }) => (
                                <RadioGroup
                                  value={value}
                                  onChange={(event, val) => {
                                    onChange(val);
                                  }}
                                  defaultValue="0"
                                  sx={{ mb: 1 }}
                                >
                                  {pricingOptions.map((option) => {
                                    return (
                                      <FormControlLabel
                                        key={option.value}
                                        value={option.value}
                                        control={<Radio />}
                                        label={option.label}
                                        sx={{
                                          "& .MuiFormControlLabel-label": {
                                            fontSize: { xs: "0.875rem", sm: "1rem" },
                                          },
                                        }}
                                      />
                                    );
                                  })}
                                </RadioGroup>
                              )}
                            />
                            {watchType === "hourly" && (
                              <Controller
                                name="hourly_rate"
                                control={control}
                                defaultValue=""
                                rules={{ required: true }}
                                render={({
                                  field: { onChange, onBlur, value, ref },
                                }) => (
                                  <StyledTextField
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          $
                                        </InputAdornment>
                                      ),
                                    }}
                                    InputLabelProps={{ style: { fontSize: 14 } }}
                                    label={typeHourlyRateLabel}
                                    value={value || ""}
                                    type="tel"
                                    fullWidth
                                    onChange={onChange}
                                    sx={{ mt: 2 }}
                                    onBlur={onBlur}
                                    error={!!errors.hourly_rate}
                                    helperText={
                                      !!errors.hourly_rate && thisFieldIsRequiredLabel
                                    }
                                  />
                                )}
                              />
                            )}
                            {watchType === "fixed" && (
                              <Controller
                                name="total_pay"
                                control={control}
                                defaultValue=""
                                render={({
                                  field: { onChange, onBlur, value, ref },
                                }) => (
                                  <StyledTextField
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          $
                                        </InputAdornment>
                                      ),
                                    }}
                                    InputLabelProps={{ style: { fontSize: 14 } }}
                                    label={enterFixedRateLabel}
                                    value={value || ""}
                                    type="tel"
                                    fullWidth
                                    onChange={onChange}
                                    sx={{ mt: 2 }}
                                    onBlur={onBlur}
                                    error={!!errors.total_pay}
                                    helperText={
                                      !!errors.total_pay && thisFieldIsRequiredLabel
                                    }
                                  />
                                )}
                              />
                            )}
                            <Controller
                              name="approximate_hours_to_complete"
                              control={control}
                              defaultValue=""
                              rules={{ required: true, min: 1 }}
                              render={({
                                field: { onChange, onBlur, value, ref },
                              }) => (
                                <StyledTextField
                                  InputLabelProps={{ style: { fontSize: 14 } }}
                                  label={estimatedAmountOfHoursLabel}
                                  value={value || ""}
                                  type="number"
                                  fullWidth
                                  onChange={onChange}
                                  sx={{ mt: 2 }}
                                  onBlur={onBlur}
                                  error={!!errors.approximate_hours_to_complete}
                                  helperText={
                                    !!errors.approximate_hours_to_complete &&
                                    thisFieldIsRequiredLabel
                                  }
                                />
                              )}
                            />
                            <Controller
                              name="mile_radius"
                              control={control}
                              defaultValue=""
                              rules={{ required: true, min: 1 }}
                              render={({
                                field: { onChange, onBlur, value, ref },
                              }) => (
                                <StyledTextField
                                  sx={{ mt: 2 }}
                                  InputLabelProps={{ style: { fontSize: 14 } }}
                                  label={worksiteRadiusMilesLabel}
                                  value={value || ""}
                                  type="number"
                                  fullWidth
                                  onChange={onChange}
                                  onBlur={onBlur}
                                  error={!!errors.mile_radius}
                                  helperText={
                                    !!errors.mile_radius
                                      ? thisFieldIsRequiredLabel
                                      : onlyAvailableToIPsWithinRadiusLabel
                                  }
                                />
                              )}
                            />
                          </Box>
                        </CardBase>
                      );
                    }

                    // Skills Card
                    if (cardId === PROJECT_EDIT_CARD_IDS.skills) {
                      return (
                        <CardBase
                          key={cardId}
                          title={whatSkillsLookingForLabel}
                          useGlassCard
                          glassEffect="clear"
                          sx={{ mt: { xs: 2, sm: 3 } }}
                          ref={cardRefs[cardId]}
                        >
                          <Box sx={{
                            px: { xs: 1, sm: 1.5 },
                            py: { xs: 2, sm: 3 },
                            "@media (orientation: landscape) and (max-width: 600px)": {
                              px: 0.5,
                              py: 1.5,
                            },
                          }}>
                            <Controller
                              name="skills"
                              control={control}
                              rules={{ required: true }}
                              render={({ field: { onChange, value } }) => (
                                <SelectSkills
                                  label={selectSearchSkillsLabel}
                                  onChange={onChange}
                                  values={value}
                                  error={!!errors.skills}
                                  helperText={
                                    !!errors.skills && thisFieldIsRequiredLabel
                                  }
                                />
                              )}
                            />
                          </Box>
                        </CardBase>
                      );
                    }

                    // Certifications Card
                    if (cardId === PROJECT_EDIT_CARD_IDS.certifications) {
                      return (
                        <CardBase
                          key={cardId}
                          title={requiredCertificationsOptionalLabel}
                          useGlassCard
                          glassEffect="clear"
                          sx={{ mt: { xs: 2, sm: 3 } }}
                          ref={cardRefs[cardId]}
                        >
                          <Box sx={{
                            px: { xs: 1, sm: 1.5 },
                            py: { xs: 2, sm: 3 },
                            "@media (orientation: landscape) and (max-width: 600px)": {
                              px: 0.5,
                              py: 1.5,
                            },
                          }}>
                            <Box>
                              <Controller
                                name="certifications"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                  <SelectCertifications
                                    label={selectCertificationsLabel}
                                    onChange={onChange}
                                    values={value}
                                  />
                                )}
                              />
                            </Box>
                          </Box>
                        </CardBase>
                      );
                    }

                    // Manager Card
                    if (cardId === PROJECT_EDIT_CARD_IDS.manager) {
                      return (
                        <CardBase
                          key={cardId}
                          title={assignWorkManagerForWorkLabel}
                          useGlassCard
                          glassEffect="clear"
                          sx={{ mt: { xs: 2, sm: 3 } }}
                          ref={cardRefs[cardId]}
                        >
                          <Box sx={{
                            px: { xs: 1, sm: 1.5 },
                            py: { xs: 2, sm: 3 },
                            "@media (orientation: landscape) and (max-width: 600px)": {
                              px: 0.5,
                              py: 1.5,
                            },
                          }}>
                            <Box>
                              <Controller
                                name="manager_id"
                                control={control}
                                rules={{ required: true }}
                                render={({ field: { onChange, value } }) => (
                                  <SelectManager
                                    label={selectWorkManagerLabel}
                                    onChange={onChange}
                                    value={value}
                                    error={!!errors.manager_id}
                                    helperText={
                                      !!errors.manager_id && thisFieldIsRequiredLabel
                                    }
                                  />
                                )}
                              />
                            </Box>
                          </Box>
                        </CardBase>
                      );
                    }

                    // Tasks Card
                    if (cardId === PROJECT_EDIT_CARD_IDS.tasks) {
                      return (
                        <CardBase
                          key={cardId}
                          title={tasksOptionalNoteLabel}
                          useGlassCard
                          glassEffect="clear"
                          sx={{ mt: { xs: 2, sm: 3 } }}
                          ref={cardRefs[cardId]}
                          headerActions={
                            <Button
                              onClick={() =>
                                setTaskModal({ open: true, data: null })
                              }
                              startIcon={<Add />}
                              size={isMobile ? "small" : "medium"}
                              sx={{
                                fontSize: { xs: "0.875rem", sm: "1rem" },
                              }}
                            >
                              {addTaskLabel}
                            </Button>
                          }
                        >
                          <Box sx={{
                            px: { xs: 1, sm: 1.5 },
                            py: { xs: 2, sm: 3 },
                            "@media (orientation: landscape) and (max-width: 600px)": {
                              px: 0.5,
                              py: 1.5,
                            },
                          }}>
                            <List>
                              {newTasks
                                .filter((it) => it._internal_op !== "delete")
                                .map((it, idx) => (
                                  <ListItem
                                    key={idx}
                                    sx={{
                                      backgroundColor: "#f5f5f5",
                                      mt: 1,
                                      p: { xs: 1.5, sm: 2 },
                                      flexDirection: { xs: "column", sm: "row" },
                                      alignItems: { xs: "flex-start", sm: "center" },
                                    }}
                                    secondaryAction={
                                      <IconButton
                                        edge="end"
                                        onClick={() => handleDeleteTask(it)}
                                        size={isMobile ? "small" : "medium"}
                                        sx={{
                                          mt: { xs: 1, sm: 0 },
                                          alignSelf: { xs: "flex-end", sm: "center" },
                                        }}
                                      >
                                        <Delete />
                                      </IconButton>
                                    }
                                  >
                                    <ListItemText
                                      primary={it.name}
                                      secondary={it.description}
                                      primaryTypographyProps={{
                                        fontSize: { xs: "0.875rem", sm: "1rem" },
                                      }}
                                      secondaryTypographyProps={{
                                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                      }}
                                    />
                                  </ListItem>
                                ))}
                            </List>
                          </Box>
                        </CardBase>
                      );
                    }

                    // Additional Information Card
                    if (cardId === PROJECT_EDIT_CARD_IDS.additionalInformation) {
                      return (
                        <CardBase
                          key={cardId}
                          title={additionalInformationLabel}
                          useGlassCard
                          glassEffect="clear"
                          sx={{ mt: { xs: 2, sm: 3 } }}
                          ref={cardRefs[cardId]}
                        >
                          <Box sx={{
                            px: { xs: 1, sm: 1.5 },
                            py: { xs: 2, sm: 3 },
                            "@media (orientation: landscape) and (max-width: 600px)": {
                              px: 0.5,
                              py: 1.5,
                            },
                          }}>
                            <Stack spacing={2}>
                              <Controller
                                name="is_photo_required"
                                control={control}
                                defaultValue={false}
                                render={({ field }) => (
                                  <FormControlLabel
                                    key="is_photo_required"
                                    control={
                                      <Checkbox {...field} checked={field.value} />
                                    }
                                    label={photosRequiredNoteLabel}
                                    sx={{
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: { xs: "0.875rem", sm: "1rem" },
                                      },
                                    }}
                                  />
                                )}
                              />
                              <Controller
                                name="is_final_notes_required"
                                control={control}
                                defaultValue={false}
                                render={({ field }) => (
                                  <FormControlLabel
                                    key="is_final_notes_required"
                                    control={
                                      <Checkbox {...field} checked={field.value} />
                                    }
                                    label={finalNotesRequiredNoteLabel}
                                    sx={{
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: { xs: "0.875rem", sm: "1rem" },
                                      },
                                    }}
                                  />
                                )}
                              />
                              <Controller
                                name="tool_required"
                                control={control}
                                defaultValue={false}
                                render={({ field }) => (
                                  <FormControlLabel
                                    key="tool_required"
                                    control={
                                      <Checkbox {...field} checked={field.value} />
                                    }
                                    label={toolsRequiredNoteLabel}
                                    sx={{
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: { xs: "0.875rem", sm: "1rem" },
                                      },
                                    }}
                                  />
                                )}
                              />
                              <Controller
                                name="background_check_required"
                                control={control}
                                defaultValue={false}
                                render={({ field }) => (
                                  <FormControlLabel
                                    key="background_check_required"
                                    control={
                                      <Checkbox {...field} checked={field.value} />
                                    }
                                    label={backgroundCheckRequiredFormLabel}
                                    sx={{
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: { xs: "0.875rem", sm: "1rem" },
                                      },
                                    }}
                                  />
                                )}
                              />
                              <Controller
                                name="drug_test_required"
                                control={control}
                                defaultValue={false}
                                render={({ field }) => (
                                  <FormControlLabel
                                    key="drug_test_required"
                                    control={
                                      <Checkbox {...field} checked={field.value} />
                                    }
                                    label={drugTestRequiredFormLabel}
                                    sx={{
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: { xs: "0.875rem", sm: "1rem" },
                                      },
                                    }}
                                  />
                                )}
                              />
                            </Stack>
                          </Box>
                        </CardBase>
                      );
                    }

                    return null;
                  })}
                  {/* <Card sx={{ mt: 1 }}>
                <CardHeader
                  titleTypographyProps={{ fontSize: 18, color: "#17903d" }}
                  title="Internal Notes"
                />
                <CardContent>
                  <Controller
                    name="internal_notes"
                    control={control}
                    defaultValue=""
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <StyledTextField
                        InputLabelProps={{ style: { fontSize: 14 } }}
                        label="Not visible to talents (optional)"
                        multiline={true}
                        minRows={4}
                        value={value || ""}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                </CardContent>
              </Card> */}
                </>
              )}

              <Stack
                direction="row"
                justifyContent={isMobile ? "stretch" : "flex-end"}
                sx={{
                  mt: { xs: 3, sm: 4 },
                  pb: { xs: 2, sm: 0 },
                }}
              >
                <StyledLoadingButton
                  type="submit"
                  variant="contained"
                  size={isMobile ? "medium" : "large"}
                  disabled={isLoading}
                  loading={adding || updating}
                  fullWidth={isMobile}
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    padding: { xs: "10px 24px", sm: "12px 32px" },
                  }}
                >
                  {isEdit ? updateLabel : createLabel}
                </StyledLoadingButton>
              </Stack>
            </Box>
          </form>
        </Grid>
      </Grid>
      <ModalTaskForm
        open={taskModal.open}
        onClose={() => setTaskModal({ open: false, data: null })}
        data={taskModal.data}
        onAdd={handleAddTask}
      />
      <LocationModalForm
        open={locationModal.open}
        onClose={() => setLocationModal({ open: false, data: null })}
        onCreated={(newId) => setValue("location", newId)}
        data={locationModal.data}
      />
      {/* <ClientModalForm
        open={clientModal.open}
        onClose={() => setClientModal({ open: false, data: null })}
        onCreated={(newId) => setValue("customer", newId)}
        data={clientModal.data}
      /> */}
      <ProjectEditCustomizeLayoutModal
        open={customizeLayoutModalOpen}
        onClose={() => setCustomizeLayoutModalOpen(false)}
        preferences={cardPreferences}
        onSave={handleSaveCardPreferences}
      />
    </Box>
  );
};
export default ProjectEditPage;
